import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ChevronDownIcon, ChevronDownSmallIcon } from '@inter-ikea-kompis/icons';
import type { ITheme } from '@inter-ikea-kompis/types';
import { IdGenerator } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Collapsible } from '@inter-ikea-kompis/component-collapsible';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';

import TextToggleLayoutEnum from '../../enums/TextToggleLayoutEnum.js';
import TextToggleSizeEnum from '../../enums/TextToggleSizeEnum.js';
import TextToggleStyle from './TextToggleStyle.js';

/**
 * Text toggle.
 *
 * @event expand Triggered when toggle is clicked
 * @event animationEnd Triggered when animation ends
 *
 * @slot Content to be toggled.
 */
export default class TextToggle extends KompisElement {
	// Layout
	@property({ type: String })
	public layout: TextToggleLayoutEnum = TextToggleLayoutEnum.subtle;

	// Label
	@property({ type: String })
	public label: string | null = null;

	// Label displayed when printing
	@property({ type: String })
	public printLabel: string | null = null;

	// Label explaining the text toggle further for screen readers. If supplied, "label" won't be read by screen readers.
	@property({ type: String })
	public override ariaLabel: string | null = null;

	// Expanded
	@property({ type: Boolean })
	public expanded = false;

	// Size
	@property({ type: String })
	public size: TextToggleSizeEnum = TextToggleSizeEnum.medium;

	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private uniqueId = new IdGenerator().generateId();
	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${TextToggleStyle.getStyle({
					expanded: this.expanded,
					layout: this.layout,
					theme: this.theme,
					printLabel: this.printLabel
				})}
			</style>
			<button
				aria-expanded="${this.expanded}"
				aria-controls="${this.uniqueId}"
				@click="${this.onClick.bind(this)}"
			>
				<kompis-icon
					.theme="${this.theme}"
					.icon="${this.size === TextToggleSizeEnum.medium
						? ChevronDownIcon
						: ChevronDownSmallIcon}"
				></kompis-icon>
				<div class="divider"></div>
				<kompis-text
					.theme="${this.theme}"
					.type="${this.size === TextToggleSizeEnum.medium
						? ThemeFontStyleTypeEnum.bodyMedium
						: ThemeFontStyleTypeEnum.bodySmall}"
					.bold="${true}"
				>
					${this.getLabel()}
				</kompis-text>
			</button>
			<kompis-collapsible
				id="${this.uniqueId}"
				.expanded="${this.expanded}"
				@animationEnd="${() =>
					this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }))}"
			>
				<slot></slot>
			</kompis-collapsible>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-focus-outline', FocusOutline);
		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-collapsible', Collapsible);

		super.connectedCallback();
	}
	private getLabel(): RenderResult {
		if (!this.ariaLabel) {
			return html`<span>${this.label}</span>`;
		} else {
			return html`<span class="visuallyHidden">${this.ariaLabel}</span
				><span aria-hidden="true">${this.label}</span>`;
		}
	}

	/**
	 * On click.
	 */
	private onClick(): void {
		this.dispatchEvent(
			new CustomEvent('expand', {
				bubbles: true,
				detail: {
					expanded: !this.expanded
				}
			})
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-text-toggle': TextToggle;
	}
}
